import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { projectsApi } from '@/api/projects'
import { usersApi } from '@/api/users'
import { ArrowLeft } from 'lucide-react'
import type { ProjectCreate } from '@/types'
import CharCounter from '@/components/common/CharCounter'
import MoneyInput from '@/components/common/MoneyInput'

const today = new Date().toISOString().split('T')[0]

const schema = z
  .object({
    // Шифр проекта — свободное текстовое поле: любые символы, без формата.
    // Единственное требование — не пустое (это идентификатор проекта) и не длиннее 50.
    code: z
      .string()
      .trim()
      .min(1, 'Укажите шифр проекта')
      .max(50, 'Максимум 50 символов'),
    name: z.string().min(3, 'Минимум 3 символа').max(300, 'Максимум 300 символов'),
    customer: z.string().min(2, 'Укажите заказчика').max(300, 'Максимум 300 символов'),
    address: z.string().min(5, 'Укажите адрес объекта').max(500, 'Максимум 500 символов'),
    start_date: z.string().min(1, 'Укажите дату начала'),
    planned_end_date: z.string().min(1, 'Укажите плановый срок'),
    chief_engineer_id: z.string().optional(),
    note: z.string().max(1000, 'Максимум 1000 символов').optional(),
    cost: z
      .string()
      .optional()
      .refine((v) => !v || (!isNaN(Number(v)) && Number(v) >= 0), {
        message: 'Введите корректную сумму',
      }),
  })
  .refine((d) => d.planned_end_date > d.start_date, {
    message: 'Срок завершения должен быть позже даты начала',
    path: ['planned_end_date'],
  })

type FormData = z.infer<typeof schema>

export default function ProjectFormPage() {
  const navigate = useNavigate()
  const [serverError, setServerError] = useState('')

  // Загружаем список сотрудников для выбора ГИП
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list().then((r) => r.data),
  })

  const chiefEngineers = users.filter((u) => u.role === 'chief_engineer' && u.is_active)

  // Полный список проектов уже доступен директору/заму — используем для
  // живой подсказки о занятом шифре без похода на сервер при каждом нажатии.
  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list().then((r) => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (data: ProjectCreate) => projectsApi.create(data),
    onSuccess: (res) => {
      navigate(`/projects/${res.data.id}`)
    },
    onError: (err: any) => {
      const status = err?.response?.status
      const detail = err?.response?.data?.detail
      if (status === 409) {
        setServerError(`Проект с таким шифром уже существует`)
      } else if (typeof detail === 'string') {
        setServerError(detail)
      } else {
        setServerError('Ошибка при создании проекта')
      }
    },
  })

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {},
  })

  const codeValue = watch('code')
  const codeConflict = projects.find((p) => p.code === codeValue?.trim())

  function onSubmit(data: FormData) {
    setServerError('')
    const payload: ProjectCreate = {
      // Шифр сохраняем как есть — без принудительного верхнего регистра
      code: data.code.trim(),
      name: data.name,
      customer: data.customer,
      address: data.address,
      start_date: data.start_date,
      planned_end_date: data.planned_end_date,
      chief_engineer_id: data.chief_engineer_id || undefined,
      cost: data.cost ? data.cost : undefined,
      note: data.note?.trim() ? data.note.trim() : undefined,
    }
    createMutation.mutate(payload)
  }

  const inputCls =
    'block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1'
  const errorCls = 'mt-1 text-xs text-rose-600'

  return (
    <div className="p-6 max-w-2xl">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft className="h-4 w-4" /> Назад
      </button>

      <h1 className="mb-6 text-xl font-semibold text-gray-900">Новый проект</h1>

      <div className="rounded-xl bg-white border border-gray-200 shadow-sm p-6">
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">

          {/* Шифр + Название */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Шифр проекта *</label>
              <input
                type="text"
                {...register('code')}
                maxLength={50}
                placeholder="ГГП-2026-01"
                className={inputCls}
              />
              <p className="mt-1 text-xs text-gray-400">Любой текст, до 50 символов</p>
              {codeConflict && (
                <p className="mt-1 text-xs text-amber-600">
                  Шифр уже используется в проекте «{codeConflict.name}» ({codeConflict.code})
                </p>
              )}
              {errors.code && <p className={errorCls}>{errors.code.message}</p>}
              <CharCounter value={codeValue} max={50} />
            </div>
            <div>
              <label className={labelCls}>Название объекта *</label>
              <input
                type="text"
                {...register('name')}
                maxLength={300}
                placeholder="Реконструкция котельной г. Тула"
                className={inputCls}
              />
              {errors.name && <p className={errorCls}>{errors.name.message}</p>}
              <CharCounter value={watch('name')} max={300} />
            </div>
          </div>

          {/* Заказчик */}
          <div>
            <label className={labelCls}>Заказчик *</label>
            <input
              type="text"
              {...register('customer')}
              maxLength={300}
              placeholder="МУП «Теплосеть» г. Тула"
              className={inputCls}
            />
            {errors.customer && <p className={errorCls}>{errors.customer.message}</p>}
            <CharCounter value={watch('customer')} max={300} />
          </div>

          {/* Адрес */}
          <div>
            <label className={labelCls}>Адрес объекта *</label>
            <input
              type="text"
              {...register('address')}
              maxLength={500}
              placeholder="г. Тула, ул. Котельная, д. 5"
              className={inputCls}
            />
            {errors.address && <p className={errorCls}>{errors.address.message}</p>}
            <CharCounter value={watch('address')} max={500} />
          </div>

          {/* Даты */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Дата начала *</label>
              <input
                type="date"
                {...register('start_date')}
                defaultValue={today}
                className={inputCls}
              />
              {errors.start_date && <p className={errorCls}>{errors.start_date.message}</p>}
            </div>
            <div>
              <label className={labelCls}>Плановый срок завершения *</label>
              <input
                type="date"
                {...register('planned_end_date')}
                className={inputCls}
              />
              {errors.planned_end_date && (
                <p className={errorCls}>{errors.planned_end_date.message}</p>
              )}
            </div>
          </div>

          {/* Стоимость проекта */}
          <div>
            <label className={labelCls}>Стоимость проекта, ₽ (договорная)</label>
            <Controller
              name="cost"
              control={control}
              render={({ field }) => (
                <MoneyInput
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="например 5 000 000"
                  className={inputCls}
                />
              )}
            />
            {errors.cost && <p className={errorCls}>{errors.cost.message}</p>}
          </div>

          {/* ГИП */}
          <div>
            <label className={labelCls}>Главный инженер проекта (ГИП)</label>
            <select {...register('chief_engineer_id')} className={inputCls}>
              <option value=""> - не назначен - </option>
              {chiefEngineers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </select>
          </div>

          {/* Примечание */}
          <div>
            <label className={labelCls}>Примечание</label>
            <textarea
              {...register('note')}
              rows={3}
              maxLength={1000}
              placeholder="Любая заметка по проекту (необязательно)"
              className={`${inputCls} resize-y`}
            />
            <CharCounter value={watch('note')} max={1000} />
          </div>

          {serverError && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
              {serverError}
            </div>
          )}

          {/* Кнопки */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isSubmitting || createMutation.isPending}
              className="rounded-lg bg-brand-600 px-5 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {createMutation.isPending ? 'Создание...' : 'Создать проект'}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-lg border border-gray-300 px-5 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
